import { useMemo, useCallback } from 'react';
import { useAppThunkDispatch } from 'state/store';
import { useSelector } from 'react-redux';
import {
  getLdTeamsList,
  getSelectedLdTeamsList,
  selectAllLdTeams,
  selectCanFetchMoreLdTeams,
  selectSelectedLdTeamIds,
  selectSelectedLdTeams,
} from 'state/ProjectsList/filters/Teams/ldTeamsSlice';
import { LdTeam } from 'types/store/normalized/ldTeam';

interface UseProjectTeamsParams {
  appliedFilters?: string[];
}

interface UseProjectTeamsReturn {
  fetchLdTeams: () => Promise<void>;
  ldTeams: LdTeam[];
  canFetchMoreLdTeams: boolean;
  selectedLdTeamIds: string[];
  selectedLdTeams: LdTeam[];
  shouldFetchLdTeams: boolean;
  fetchSelectedLdTeams: (selected: string[]) => Promise<void>;
}

export const useProjectTeams = ({
  appliedFilters,
}: UseProjectTeamsParams): UseProjectTeamsReturn => {
  const dispatch = useAppThunkDispatch();
  const ldTeams = useSelector(selectAllLdTeams);
  const canFetchMoreLdTeams = useSelector(selectCanFetchMoreLdTeams);
  const selectedLdTeamIds = useSelector(selectSelectedLdTeamIds);
  const selectedLdTeams = useSelector(selectSelectedLdTeams);

  const fetchLdTeams = useCallback(async () => {
    await dispatch(getLdTeamsList());
  }, [dispatch]);

  const fetchSelectedLdTeams = useCallback(
    async (selected: string[]) => {
      await dispatch(getSelectedLdTeamsList({ selectedLdTeamIds: selected }));
    },
    [dispatch]
  );

  const shouldFetchLdTeams = useMemo(() => {
    if (appliedFilters && appliedFilters.length > 0) {
      return (
        selectedLdTeams.length === 0 ||
        appliedFilters.length !== selectedLdTeamIds.length
      );
    }
    return ldTeams.length === 0;
  }, [
    appliedFilters,
    ldTeams.length,
    selectedLdTeamIds.length,
    selectedLdTeams.length,
  ]);

  return useMemo(
    () => ({
      fetchLdTeams,
      ldTeams,
      canFetchMoreLdTeams,
      selectedLdTeamIds,
      selectedLdTeams,
      shouldFetchLdTeams,
      fetchSelectedLdTeams,
    }),
    [
      fetchLdTeams,
      ldTeams,
      canFetchMoreLdTeams,
      selectedLdTeamIds,
      selectedLdTeams,
      shouldFetchLdTeams,
      fetchSelectedLdTeams,
    ]
  );
};
